/**
 * dialogue-export.js — render what /dialogue-enhancer is playing into a file.
 *
 * The player is the preview; this is the delivery. It re-runs the SAME graph
 * (dialogue-enhancer.js on an OfflineAudioContext, same worklet, same preset)
 * so the saved file sounds like what the user auditioned — a second, "export
 * quality" chain would drift from the preview and every drift is a bug report.
 *
 *   file ──FFmpeg──► audio.wav (native ch, 48 k)
 *                        │
 *                  decodeAudioData
 *                        │
 *          OfflineAudioContext + DialogueEnhancer.attachBuffer
 *                        │  (drop the leading worklet latency)
 *                   stereo AudioBuffer
 *                        │
 *                   WAV (s16) ──FFmpeg──► -c:v copy + AAC 192k ──► mp4 / mkv
 *
 * Why WAV in the middle instead of WebCodecs AudioEncoder: the mux pass has to
 * run in FFmpeg anyway (`-c:v copy` is the whole point — no video re-encode),
 * and handing it a WAV costs one AAC encode inside a pass we already pay for.
 * An AudioEncoder detour would add a second code path for zero saved work.
 *
 * CEILING — memory-bound, and the number is measured rather than reasoned
 * about: see maxSeconds(). The UI states it BEFORE the user starts, because a
 * tab that dies at 80 % has spent their minutes and told them nothing. It also
 * states the expected wait, from the same two measurements.
 */
(function (w) {
'use strict';
if (w.DialogueExport) return;

var SR = 48000;

/**
 * How long a file this device can actually see through, in seconds.
 *
 * CALIBRATED, not guessed (2026-08-27, Chromium, navigator.deviceMemory = 8):
 *   20 min stereo → 132 s, completed
 *   60 min stereo → 403 s, completed
 * The first version of this function derived a limit from first principles and
 * said 15 min — a third of what the same machine demonstrably does. The
 * constant below is set so that machine lands just INSIDE its proven point
 * (59 min against a measured 60): the promise stops where the evidence stops
 * rather than where an extrapolation feels safe.
 *
 * Two buffers are live at the peak — the decoded source and the render — and
 * the source is dropped before the output WAV is built (see run()). The output
 * WAVs are Blobs, which do not sit in this budget.
 */
function maxSeconds(channels) {
    var ch = Math.max(1, Math.min(8, channels || 2));
    var gbRam = Math.min(8, (navigator.deviceMemory || 4));
    var budgetBytes = gbRam * 1024 * 1024 * 1024 * 0.32;   // → 59 min at 8 GB stereo
    var perSec = SR * (ch * 4 + 2 * 4);   // decoded f32 (ch) + rendered f32 (2)
    return Math.max(60, Math.floor(budgetBytes / perSec));
}

/** Seconds of work per second of video — same two measurements as above. */
var SEC_PER_SEC = 0.112;

function outContainer(name) {
    var ext = String(name || '').split('.').pop().toLowerCase();
    // MP4 only where the source already is one; MKV swallows every video
    // codec a browser can play, so it is the safe home for webm/mkv/avi.
    return /^(mp4|m4v|mov)$/.test(ext) ? 'mp4' : 'mkv';
}

/** AudioBuffer → 16-bit PCM WAV Blob (interleaved, little-endian). */
function bufferToWav(buf, fromFrame, maxFrames) {
    var ch = buf.numberOfChannels, start = fromFrame || 0;
    // maxFrames — длина исходника: рендер идёт с запасом на задержку, и без
    // потолка звуковой файл выходил на ~0.2 с длиннее записи (тишина в хвосте).
    var frames = Math.max(0, Math.min(maxFrames || Infinity, buf.length - start));
    var bytes = frames * ch * 2;
    var ab = new ArrayBuffer(44 + bytes);
    var v = new DataView(ab);
    function str(o, s) { for (var i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); }
    str(0, 'RIFF'); v.setUint32(4, 36 + bytes, true); str(8, 'WAVE');
    str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true);
    v.setUint16(22, ch, true); v.setUint32(24, buf.sampleRate, true);
    v.setUint32(28, buf.sampleRate * ch * 2, true); v.setUint16(32, ch * 2, true);
    v.setUint16(34, 16, true); str(36, 'data'); v.setUint32(40, bytes, true);
    var chans = [], c;
    for (c = 0; c < ch; c++) chans.push(buf.getChannelData(c));
    var o = 44;
    for (var i = 0; i < frames; i++) {
        for (c = 0; c < ch; c++) {
            var s = chans[c][start + i];
            s = s < -1 ? -1 : s > 1 ? 1 : s;
            v.setInt16(o, s < 0 ? s * 0x8000 : s * 0x7fff, true);
            o += 2;
        }
    }
    return new Blob([ab], { type: 'audio/wav' });
}

/**
 * run(opts) → Promise<{ blob, name, ms, seconds }>
 *
 * opts = {
 *   file, durationSec, mode: 'video'|'audio',
 *   settings: { preset, voiceDb, musicDb, night, boost },
 *   workletUrls: [...],
 *   baseName,                     // suggested stem, already sanitised
 *   onPhase(phase, ratio)         // 'extract'|'render'|'mux', ratio 0..1|null
 * }
 */
function run(opts) {
    opts = opts || {};
    var file = opts.file;
    var onPhase = opts.onPhase || function () {};
    var t0 = Date.now();
    var cancelled = false;
    var progHandler = null;

    function ffProgress(phase) {
        detach();
        progHandler = function (ev) {
            var p = ev && ev.detail;
            onPhase(phase, typeof p === 'number' ? Math.max(0, Math.min(1, p)) : null);
        };
        w.addEventListener('ffmpeg-progress', progHandler);
    }
    function detach() {
        if (progHandler) { w.removeEventListener('ffmpeg-progress', progHandler); progHandler = null; }
    }

    /**
     * Pull the audio out in `kind`, decode it, and if the browser refuses the
     * bytes try the other container once before giving up. The preference is
     * chosen by channel count above; this retry is the safety net for the
     * combinations nobody has measured yet — a decoder that says no is a fact
     * about this browser, not about the file.
     */
    function extractAudio(src, kind, isRetry) {
        var args = kind === 'm4a'
            ? ['-vn', '-c:a', 'aac', '-b:a', '320k']
            : ['-vn', '-c:a', 'pcm_s16le', '-ar', String(SR)];
        return w.FFmpegProcessor.processStreaming(src, args, 'de-src.' + kind)
            .then(function (res) {
                detach();
                if (cancelled) throw abortErr();
                return res.blob ? res.blob.arrayBuffer()
                                : fetch(res.url).then(function (r) { return r.arrayBuffer(); });
            })
            .then(function (ab) {
                if (cancelled) throw abortErr();
                onPhase('render', null);
                var AC = w.AudioContext || w.webkitAudioContext;
                var probe = new AC();
                return probe.decodeAudioData(ab).then(function (buf) {
                    try { probe.close(); } catch (_) {}
                    return buf;
                }, function (e) {
                    try { probe.close(); } catch (_) {}
                    throw e;
                });
            })
            .catch(function (err) {
                if (isRetry || cancelled) throw err;
                onPhase('extract', 0);
                ffProgress('extract');
                return extractAudio(src, kind === 'wav' ? 'm4a' : 'wav', true);
            });
    }

    var p = Promise.resolve().then(function () {
        if (!w.FFmpegProcessor) throw new Error('NO_FFMPEG');
        return w.FFmpegProcessor.initWithProgress(null, null, {});
    }).then(function (ok) {
        if (!ok) throw new Error('NO_FFMPEG');
        onPhase('extract', 0);
        ffProgress('extract');
        // The channel count is kept, never folded to stereo: a 5.1 source has
        // its dialogue in a discrete centre channel, and that is the one case
        // where the separation is exact rather than estimated.
        //
        // But the container has to be one the BROWSER can decode, and those are
        // not the same set. Measured 2026-08-28 on a 5.1 fixture: FFmpeg writes
        // a perfectly valid 6-channel pcm_s16le WAV and decodeAudioData answers
        // "Unable to decode audio data"; the same audio as 6-channel AAC decodes
        // to 6 channels without complaint (and is 14× smaller, which matters at
        // feature length). So WAV for one or two channels — lossless, and the
        // common case — and M4A above that.
        return extractAudio(file, (opts.channels || 2) > 2 ? 'm4a' : 'wav');
    }).then(function (buf) {
        if (cancelled) throw abortErr();

        /* The neural engine, when the caller asked for it and the file is short
           enough to be worth the wait. Everything around this step is shared
           with the fast path — same extract, same mux, same naming — because
           two export pipelines would drift and only one of them would get the
           next bug fix. Only the separation differs.

           The stems come back at the SEPARATOR's rate (44.1 kHz for Kim), not
           the source's; the WAV writer reads `sampleRate` off the buffer, so
           that travels correctly as long as nothing here assumes 48 k. */
        if (opts.engine === 'ai' && w.DialogueAi) {
            onPhase('render', null);
            return w.DialogueAi.separateBuffer(buf, function (frac, stage) {
                onPhase('render', typeof frac === 'number' ? frac : null, stage);
            }).then(function (stems) {
                if (cancelled) throw abortErr();
                var s2 = opts.settings || {};
                var off2 = s2.preset === 'off';
                var gv = Math.pow(10, (off2 ? 0 : (s2.voiceDb || 0)) / 20);
                var gi = Math.pow(10, (off2 ? 0 : (s2.musicDb || 0)) / 20);
                var boost = Math.min(3, Math.max(1, s2.boost || 1));
                var V = stems.vocal, I = stems.instrumental;
                var n = Math.min(V.length, I.length), chs = 2;
                var mix = { numberOfChannels: chs, length: n, sampleRate: V.sampleRate, _d: [] };
                for (var c = 0; c < chs; c++) {
                    var vd = V.getChannelData(Math.min(c, V.numberOfChannels - 1));
                    var id = I.getChannelData(Math.min(c, I.numberOfChannels - 1));
                    var o = new Float32Array(n);
                    for (var i = 0; i < n; i++) {
                        var y = (vd[i] * gv + id[i] * gi) * boost;
                        o[i] = y > 1 ? 1 : (y < -1 ? -1 : y);   // the DSP path has a limiter; this is its stand-in
                    }
                    mix._d.push(o);
                }
                mix.getChannelData = function (c) { return this._d[c]; };
                w.__deExportDebug = { engine: 'ai', srcRate: buf.sampleRate, outRate: mix.sampleRate, frames: n };
                return { rendered: mix, lat: 0, frames: n };
            });
        }

        // Head-room for the worklet's own latency: render longer, then drop it.
        var pad = Math.ceil(buf.sampleRate * 0.25);
        var OAC = w.OfflineAudioContext || w.webkitOfflineAudioContext;
        var off = new OAC(2, buf.length + pad, buf.sampleRate);
        var eng = w.DialogueEnhancer.create({
            slug: 'dialogue-enhancer-export',
            ctx: off,
            workletUrls: opts.workletUrls
        });
        var s = opts.settings || {};
        eng.state.preset = s.preset || 'medium';
        eng.state.voiceDb = s.voiceDb;
        eng.state.musicDb = s.musicDb;
        eng.state.nightUser = !!s.night;
        eng.state.boost = Math.min(3, Math.max(1, s.boost || 1));
        return eng.attachBuffer(buf).then(function () {
            return off.startRendering();
        }).then(function (rendered) {
            // latencyFrames only lands after rendering (port messages flush at
            // the end) — read it here, not before.
            var est = eng.getState();
            var frames = buf.length;
            w.__deExportDebug = {
                mode: est.mode, channels: est.channels, lat: est.latencyFrames,
                srcCh: buf.numberOfChannels, srcRate: buf.sampleRate, degraded: est.degraded,
                preset: est.preset, voiceDb: est.voiceDb, musicDb: est.musicDb
            };
            // Drop the DECODED source before building the WAV. Both buffers are
            // float32 and the same length, so holding the input while writing
            // the output doubles the peak for no reason — and peak memory is
            // exactly what caps how long a file this tool can save.
            eng.destroy();
            buf = null;
            // С головы срезается ВСЯ задержка цепочки, а не только ворклета (2026-10-09):
            // лимитер на мастере (упреждение 6 мс) и выравнивание речи/музыки —
            // ещё столько же. Без них звук ложился на видео на 6–12 мс позже.
            var cl = eng._compLat || 0;
            return { rendered: rendered, lat: (est.latencyFrames || 0) + cl * (est.degraded ? 1 : 2), frames: frames };
        });
    }).then(function (r) {
        if (cancelled) throw abortErr();
        // A named File, not a bare Blob: the WORKERFS mount derives the input
        // name (and therefore the demuxer hint) from it.
        var wav = new File([bufferToWav(r.rendered, r.lat, r.frames)], 'de-mix.wav', { type: 'audio/wav' });
        onPhase('mux', 0);
        ffProgress('mux');
        var stem = opts.baseName || 'video';
        function outName(ext){
            // The naming primitive owns sanitising, the Windows reserved names
            // and the byte cap (Cyrillic is two bytes a letter — a stem that
            // looks short can still overflow).
            var n = w.__outputName ? w.__outputName.join(stem, ext) : (stem + '.' + ext);
            return n || ('video.' + ext);
        }
        if (opts.mode === 'audio') {
            return w.FFmpegProcessor.processStreaming(
                wav, ['-c:a', 'aac', '-b:a', '192k'], outName('m4a'), 'de-mix.wav'
            ).then(function (out) { return { out: out, name: outName('m4a') }; });
        }
        var name = outName(outContainer(file.name));
        return w.FFmpegProcessor.processMultiStreaming(
            [file, wav],
            ['-map', '0:v:0', '-map', '1:a:0', '-c:v', 'copy', '-c:a', 'aac',
             '-b:a', '192k', '-shortest'],
            name
        ).then(function (out) { return { out: out, name: name }; });
    }).then(function (r) {
        detach();
        var blob = r.out.blob || r.out;
        return { blob: blob, name: r.name, ms: Date.now() - t0, size: blob.size };
    }, function (err) {
        detach();
        /* A cancel mid-FFmpeg used to rethrow the raw terminate error (no
           .aborted, no name) — the page showed «save failed» for the user's own
           Cancel. */
        throw cancelled ? abortErr() : err;
    });

    p.cancel = function () {
        cancelled = true;
        try { w.FFmpegProcessor.abort(); } catch (_) {}
    };
    return p;
}

function abortErr() {
    var e = new Error('aborted');
    e.name = 'AbortError';
    e.aborted = true;
    return e;
}

w.DialogueExport = {
    run: run,
    maxSeconds: maxSeconds,
    secPerSec: SEC_PER_SEC,
    outContainer: outContainer,
    // exposed for the measurement harness (tests/scratch/de-export-*.mjs)
    _bufferToWav: bufferToWav
};
})(window);
